import React, { Component } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, TextField, Typography } from "@material-ui/core";
import { withAlert } from "react-alert";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import Button from "../../waybee-ui/Button";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import UserService from "../../services/UserService";
import { passwordPolicyErrors, passwordPolicyHint } from "../../utils/passwordPolicy";

const PageHeader = styled.div`
  margin-bottom: 8px;
`;

const PageSubtitle = styled(Typography)`
  && {
    color: #6b6b6b;
    margin-top: 4px;
    margin-bottom: 16px;
  }
`;

const Form = styled.form`
  max-width: 480px;
`;

class ChangePassword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lastPassword: "",
      newPassword: "",
      confirmationPassword: "",
      loading: false,
      error: ""
    };
  }

  handleChange = field => event => {
    this.setState({ [field]: event.target.value, error: "" });
  };

  handleSubmit = async event => {
    event.preventDefault();
    const { lastPassword, newPassword, confirmationPassword } = this.state;
    const { t, alert } = this.props;

    if (!lastPassword || !newPassword) {
      this.setState({ error: t("login.changePassword.required") });
      return;
    }
    if (newPassword !== confirmationPassword) {
      this.setState({ error: t("login.confirmMismatch") });
      return;
    }
    const policy = passwordPolicyErrors(newPassword, t);
    if (policy.length) {
      this.setState({ error: policy[0] });
      return;
    }
    if (newPassword === lastPassword) {
      this.setState({ error: t("login.passwordPolicy.different") });
      return;
    }

    this.setState({ loading: true, error: "" });
    try {
      await UserService.changePassword({
        lastPassword,
        newPassword,
        confirmationPassword
      });
      alert.success(t("login.changePassword.success"));
      this.setState({
        lastPassword: "",
        newPassword: "",
        confirmationPassword: "",
        loading: false
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: (err && err.message) || t("login.changePassword.error")
      });
    }
  };

  render() {
    const {
      lastPassword,
      newPassword,
      confirmationPassword,
      loading,
      error
    } = this.state;
    const { t } = this.props;

    return (
      <Box p={3}>
        <Group>
          <PageHeader>
            <Heading level={5}>{t("login.changePassword.title")}</Heading>
            <PageSubtitle>{t("login.changePassword.subtitle")}</PageSubtitle>
          </PageHeader>
          <Form onSubmit={this.handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              type="password"
              label={t("login.currentPassword")}
              value={lastPassword}
              onChange={this.handleChange("lastPassword")}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              type="password"
              label={t("login.newPassword")}
              value={newPassword}
              onChange={this.handleChange("newPassword")}
              helperText={passwordPolicyHint(t)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              type="password"
              label={t("login.confirmPassword")}
              value={confirmationPassword}
              onChange={this.handleChange("confirmationPassword")}
            />
            {error ? (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            ) : null}
            <Box mt={3}>
              {loading ? (
                <CircularProgress size={28} />
              ) : (
                <Button type="submit" variant="contained" color="primary">
                  {t("login.changePassword.submit")}
                </Button>
              )}
            </Box>
          </Form>
        </Group>
      </Box>
    );
  }
}

ChangePassword.propTypes = {
  t: PropTypes.func.isRequired,
  alert: PropTypes.shape({
    success: PropTypes.func
  }).isRequired
};

export default withAlert()(withTranslation()(ChangePassword));
